import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { api } from "./lib/api";
import Login from "./pages/Login";
import Apply from "./pages/Apply";
import Today from "./pages/Today";
import Dashboard from "./pages/Dashboard";
import Prospects from "./pages/Prospects";
import FormManager from "./pages/FormManager";
import Campaigns from "./pages/Campaigns";
import Layout from "./components/Layout";
import BrandBackground from "./components/BrandBackground";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    api
      .post("/auth/session", null, { headers: { "X-Session-ID": sessionId } })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", "/today");
        navigate("/today", { replace: true, state: { user: data } });
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Authentication failed");
      });
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-panel p-8 max-w-md text-center" data-testid="auth-error-panel">
          <p className="eyebrow mb-2">Access Denied</p>
          <p className="text-slate-300 text-sm mb-4">{error}</p>
          <button
            data-testid="auth-error-back-btn"
            onClick={() => (window.location.href = "/")}
            className="px-4 py-2 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-sm font-semibold transition-colors"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="eyebrow">Authenticating…</p>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="eyebrow">Loading system…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/apply" element={<Apply />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/today" element={<Today />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/prospects" element={<Prospects />} />
        <Route path="/form" element={<FormManager />} />
        <Route path="/campaigns" element={<Campaigns />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrandBackground />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
